import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  IconButton,
  Paper,
  Box,
} from "@mui/material";
import Fab from "@mui/material/Fab";
import dayjs from 'dayjs';
import Stack from '@mui/material/Stack';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import Backdrop from "@mui/material/Backdrop";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CreateIcon from "@mui/icons-material/Create";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import Swal from "sweetalert2";

import Loading from "../Loading/Loading";
import AddTimesheetEntry from "../AddTimesheetEntry/AddTimesheetEntry";
import LineInformation from "./LineInformation";

export default function Timesheet() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setLoading] = useState(true);

  const [weekInfo, setWeekInfo] = useState(null);
  const [entries, setEntries] = useState(null);
  const [projects, setProjects] = useState(null);
  const [tasks, setTasks] = useState(null);

  const [selectedEntry, setSelectedEntry] = useState(null);
  const [viewedEntry, setViewedEntry] = useState(null);

  const [addTimesheetEntryOpen, setAddTimesheetEntryOpen] = useState(false);
  const [lineInformationOpen, setLineInformationOpen] = useState(false);

  const [disabledDays, setDisabledDays] = useState([]);
  const [dateValue, setDateValue] = useState(dayjs(new Date().toISOString()));
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);

  const [loadData, setLoadData] = useState(new Date().toISOString());

  const [open, setOpen] = useState(false);
  const [shouldOpen, setShouldOpen] = useState(true);
  const handleOpen = () => {
    if (!shouldOpen) {
      setShouldOpen(true);
    } else {
      setOpen(true);
    }
  };

  const handleClose = () => setOpen(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let response = await axios.get(
          `/api/timesheet?id=${searchParams.get("id")}`
        );
        if (response.data.error) {
          navigate(`/login`, { replace: false });
        }
        setWeekInfo(response.data.week);
        setEntries(response.data.entries);
        setProjects(response.data.projects);
        setTasks(response.data.tasks);

        let summaryData = await axios.get("/api/summary");
        let daysCreated = summaryData.data.daysCreated;
        let modifidDaysCreated = [];
        daysCreated.forEach(item => {
            let day = item.split("T")[0]
            modifidDaysCreated.push(day);
        })
        setDisabledDays(modifidDaysCreated);
        setLoading(false);
      } catch (ex) {
        console.log(ex)
        localStorage.setItem(Date.now(), JSON.stringify(ex));
        if (ex.error && ex.oldestData) {
          Swal.fire({
            title: "Error",
            html: ex.message,
            icon: "error",
            confirmButtonColor: "rgb(119, 198, 110)",
          });
          navigate(-1);
        }
      }
    };
    fetchData();
  }, [loadData]);

  const getTotalWeekHours = (obj) => {
    let sum = 0;
    for (const key in obj) {
      sum += obj[key];
    }
    return sum;
  };

  const disableDays = params => {
    if ((params.$D === 1 || params.$W === 1) && !disabledDays.includes(params.$d.toISOString().split("T")[0])) {
        return false;
    } else {
        return true;
    }
}

  const editEntry = (entry) => {
    setSelectedEntry(entry);
    setAddTimesheetEntryOpen(true);
  };

  const getNextTimesheet = async () => {
    setLoading(true);
    try {
      let resp = await axios.get(
        `/api/nexttimesheet?id=${searchParams.get("id")}`
      );
      if (resp && resp.data && resp.data.data && resp.data.data.id) {
        navigate(`/timesheet?id=${resp.data.data.id}`, { replace: false });
        setLoadData(new Date().toISOString());
        setLoading(false);
      } else if (resp.data.lastTimesheet) {
        Swal.fire({
          title: "Confirm ",
          html: "You have reached the last timesheet.<br> Do you want to create a timesheet for following week?",
          icon: "warning",
          showCancelButton: true,
          focusCancel: true,
          confirmButtonColor: "rgb(119, 198, 110)",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, create it",
        }).then((result) => {
          if (result.isConfirmed) {
            axios
              .post("/api/createNewWeek", { lastId: searchParams.get("id") })
              .then((response) => {
                navigate(`/timesheet?id=${response.data.nextId}`, {
                  replace: false,
                });
                setLoadData(new Date().toISOString());
                setTimeout(() => setLoading(false), 200);
              })
              .catch(function (error) {
                console.log(error);
                setLoading(false);
              });
          } else {
            setLoading(false);
          }
        });
      }
    } catch (ex) {
      setLoading(false);
      console.log(ex);
    }
  };

  const getPreviousTimesheet = async () => {
    setLoading(true);
    try {
      let resp = await axios.get(
        `/api/previoustimesheet?id=${searchParams.get("id")}`
      );
      console.log(resp.data);
      if (resp && resp.data && resp.data.data && resp.data.data.id) {
        navigate(`/timesheet?id=${resp.data.data.id}`);
        setLoadData(new Date().toISOString());
        setTimeout(() => setLoading(false), 200);
      }
    } catch (ex) {
      console.log(ex);
      setLoading(false);
    }
  };

  const submitTimeSheet = async () => {
    setShouldOpen(false);
    try {
      await axios.get(`/api/validatesubmit/${searchParams.get("id")}`);
      try {
        let response = await axios.post(
          `/api/submit/${searchParams.get("id")}`
        );
        Swal.fire({
          title: "Timesheet submitted!",
          icon: "success",
          confirmButtonColor: "rgb(119, 198, 110)",
        });
        setLoadData(new Date().toISOString());
        console.log(response);
      } catch (ex) {
        Swal.fire({
          title: "Error",
          html: ex.response.data.message,
          icon: "error",
          confirmButtonColor: "rgb(119, 198, 110)",
        });
      }
    } catch (ex) {
      console.log(ex);
      Swal.fire({
        title: "Error",
        html: ex.message,
        icon: "error",
        confirmButtonColor: "rgb(119, 198, 110)",
      });
    }
    setLoadData(new Date().toISOString());
  };

  const unsubmitTimesheet = useCallback(async () => {
    setShouldOpen(false);
    try {
      await axios.post(`/api/undosubmit/${searchParams.get("id")}`);
      Swal.fire({
        title: "Timesheet unsubmitted",
        text: "The timesheet can now be modified by you again",
        icon: "success",
        confirmButtonColor: "rgb(119, 198, 110)",
      });
    } catch (ex) {
      Swal.fire({
        title: "Error",
        html: ex.response.data.message,
        icon: "error",
        confirmButtonColor: "rgb(119, 198, 110)",
      });
    }
    handleClose();
    setLoadData(new Date().toISOString());
  });

  const insertTemplateLine = async () => {
    setShouldOpen(false);
    axios.post("/api/insertTemplateLine", { timesheetId: searchParams.get("id") })
      .then((result) => {
        handleClose();
        setLoadData(new Date().toISOString());
      }).catch(err => {
        Swal.fire({
          title: "Error",
          html: err.message,
          icon: "error",
          confirmButtonColor: "rgb(119, 198, 110)",
        });
        handleClose();
        setLoadData(new Date().toISOString());
      })
  };

  const duplicateWeek = async () => {
    setDatePickerOpen(true);
  }

  const handleChange = (newValue) => {
    setDateValue(newValue);
};

const acceptDate = () => {
  axios.post('/api/duplicateTimesheet', { timesheetId: searchParams.get("id"), date: dateValue.format() })
      .then(function (response) {
        handleClose();
        setLoadData(new Date().toISOString());
        //navigate(`/timesheets`, { replace: false });        
        navigate(`/timesheet?id=${response.data.data.id}`);
      })
      .catch(function (error) {
          Swal.fire({
              title: 'Error',
              html: error.message,
              icon: 'error',
              confirmButtonText: 'Ok'
          })
      });
}

  const deleteEntry = (entry) => {
    setLineInformationOpen(false);
    Swal.fire({
      title: "Are you sure?",
      html:
        "You are about to delete the entry <br><strong>" +
        entry.projectName +
        "</strong>",
      icon: "warning",
      showCancelButton: true,
      focusCancel: true,
      confirmButtonColor: "rgb(119, 198, 110)",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it",
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(`/api/timesheetentry/${entry.lineId}`).then((res) => {
          if (res.data.error) {
            Swal.fire("Error", res.data.message, "error");
          } else {
            axios
              .get(`/api/timesheet?id=${searchParams.get("id")}`)
              .then((resp) => {
                if (resp.data.error) {
                  navigate(`/login`, { replace: false });
                }
                setWeekInfo(resp.data.week);
                setEntries(resp.data.entries);
                setProjects(resp.data.projects);
                setTasks(resp.data.tasks);
                setLoading(false);
                Swal.fire("Success", "Entry has been deleted", "success");
              });
          }
        });
      }
      setLoading(false);
    });
  };

  const deleteTimesheet = () => {
    Swal.fire({
      title: "Confirm Deletion",
      html: `Do you want to delete the timesheet for week ${weekInfo.weekNo} (${weekInfo.period})?`,
      icon: "warning",
      showCancelButton: true,
      focusCancel: true,
      confirmButtonColor: "rgb(119, 198, 110)",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it",
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(`/api/timesheet/${searchParams.get("id")}`).then((res) => {
          if (res.data.error) {
            Swal.fire("Error", res.data.message, "error");
          } else {
            window.location.href = "/";
          }
        });
      }
    });
  };

  const sumValues = (obj) => Object.values(obj).reduce((a, b) => a + b, 0);

  const closeModal = () => {
    setSelectedEntry(null);
    setViewedEntry(null);

    if (addTimesheetEntryOpen) {
      setAddTimesheetEntryOpen(false);
    }
    if (lineInformationOpen) {
      setLineInformationOpen(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <div>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={3} style={{ width: 400 }}>
            <MobileDatePicker
              label="Pick a date"
              open={isDatePickerOpen}
              onAccept={acceptDate}
              onClose={() => setDatePickerOpen(false)}
              shouldDisableDate={dateParam => disableDays(dateParam)}
              inputFormat="DD/MM/YYYY"
              value={dateValue}
              onChange={handleChange}
              renderInput={(params) => null}
            />
          </Stack>
        </LocalizationProvider>
        <AddTimesheetEntry
          open={addTimesheetEntryOpen}
          entry={selectedEntry}
          tasks={tasks}
          projects={projects}
          registeredHours={weekInfo.registeredHours}
          week={weekInfo}
          handleClose={closeModal}
        />
        <LineInformation
          open={lineInformationOpen}
          entry={viewedEntry}
          weekInfo={weekInfo}
          editEntry={editEntry}
          deleteEntry={deleteEntry}
          timesheetstatus={weekInfo.status.toLowerCase()}
          handleClose={closeModal}
        />
        <Grid
          container
          spacing={0}
          direction="column"
          alignItems="center"
          // justify="center"
          style={{ marginTop: 10, width: "100%" }}
        >
          <Card style={{ width: "100%" }}>
            <CardContent>
              <Typography variant="h6" component="h2">
                Week {weekInfo.weekNo} ({weekInfo.period})
              </Typography>
              <Typography variant="body2" component="p">
                Status:{" "}
                <strong>
                  {weekInfo.status.charAt(0).toUpperCase() +
                    weekInfo.status.slice(1)}
                </strong>
              </Typography>
              <Typography variant="body2" component="p">
                Expected hours: <strong>{weekInfo.totalExpectedHours}</strong>
              </Typography>

              <Typography variant="body2" component="p">
                Remaining hours: <strong>{weekInfo.remainingHours}</strong>
              </Typography>
              {sumValues(weekInfo.registeredHours) > 0 && (
                <>
                  <hr />
                  <table style={{ width: "100%", marginLeft: "-8px" }}>
                    <thead>
                      <tr>
                        {weekInfo.showMon && <th style={{ color: weekInfo.isWorkingMon ? "rgba(0, 0, 0, 0.87)": "red"}}>Mon</th>}
                        {weekInfo.showTue && <th style={{ color: weekInfo.isWorkingTue ? "rgba(0, 0, 0, 0.87)": "red"}}>Tue</th>}
                        {weekInfo.showWed && <th style={{ color: weekInfo.isWorkingWed ? "rgba(0, 0, 0, 0.87)": "red"}}>Wed</th>}
                        {weekInfo.showThu && <th style={{ color: weekInfo.isWorkingThu ? "rgba(0, 0, 0, 0.87)": "red"}}>Thu</th>}
                        {weekInfo.showFri && <th style={{ color: weekInfo.isWorkingFri ? "rgba(0, 0, 0, 0.87)": "red"}}>Fri</th>}
                        {weekInfo.showSat && <th style={{ color: "red" }}>Sat</th>}
                        {weekInfo.showSun && <th style={{ color: "red" }}>Sun</th>}
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ textAlign: "center" }}>
                        {weekInfo.showMon && (
                          <td
                            style={{ visibility: weekInfo.registeredHours.monday === 0 ? "hidden" : "visible" }}>
                            {weekInfo.registeredHours.monday}
                          </td>
                        )}
                        {weekInfo.showTue && (
                          <td
                            style={{ visibility: weekInfo.registeredHours.tuesday === 0 ? "hidden" : "visible" }}>
                            {weekInfo.registeredHours.tuesday}
                          </td>
                        )}
                        {weekInfo.showWed && (
                          <td
                            style={{ visibility: weekInfo.registeredHours.wednesday === 0 ? "hidden" : "visible" }}>
                            {weekInfo.registeredHours.wednesday}
                          </td>
                        )}
                        {weekInfo.showThu && (
                          <td
                            style={{ visibility: weekInfo.registeredHours.thursday === 0 ? "hidden" : "visible" }}>
                            {weekInfo.registeredHours.thursday}
                          </td>
                        )}
                        {weekInfo.showFri && (
                          <td
                            style={{ visibility: weekInfo.registeredHours.friday === 0 ? "hidden" : "visible" }}>
                            {weekInfo.registeredHours.friday}
                          </td>
                        )}
                        {weekInfo.showSat && (
                          <td
                            style={{ visibility: weekInfo.registeredHours.saturday === 0 ? "hidden" : "visible" }}>
                            {weekInfo.registeredHours.saturday}
                          </td>
                        )}
                        {weekInfo.showSun && (
                          <td
                            style={{ visibility: weekInfo.registeredHours.sunday === 0 ? "hidden" : "visible" }}>
                            {weekInfo.registeredHours.sunday}
                          </td>
                        )}

                      </tr>
                    </tbody>
                  </table>
                </>
              )}
            </CardContent>
          </Card>
          <br />
          <Grid container spacing={2} style={{ width: "95%" }}></Grid>
          {entries.length > 0 && (
            <TableContainer component={Paper} sx={{ marginBottom: 15 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Project</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Total Hours</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry, i) => (
                    <TableRow
                      key={i}
                      onClick={(e) => {
                        setViewedEntry(entry);
                        setLineInformationOpen(true);
                      }}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      {entry.projectName && (
                        <TableCell component="th" scope="row">
                          <Typography color="rgb(172, 5, 51)">
                            {entry.projectName}
                          </Typography>
                          <Typography
                            color="textSecondary"
                            style={{ fontSize: "0.8rem" }}
                          >
                            {entry.description.replace(/(.{31})..+/, "$1…")}
                          </Typography>
                        </TableCell>
                      )}
                      {!entry.projectName && (
                        <TableCell component="th" scope="row">
                          <Typography color="rgb(172, 5, 51)">
                            <WarningIcon />
                            <span>No Project Name</span>
                          </Typography>
                          <Typography
                            color="textSecondary"
                            style={{ fontSize: "0.8rem" }}
                          >
                            No Description
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {getTotalWeekHours(entry.registeredHours)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {entries.length === 0 && (
            <div
              style={{ display: "flex", alignItems: "center", width: "90%" }}
            >
              <InfoIcon
                fontSize="large"
                style={{ marginRight: 10, color: "rgb(119, 198, 110)" }}
              />
              <p>
                Press the green button in the lower right corner to add a new
                entry{" "}
              </p>
            </div>
          )}
        </Grid>
        <Box sx={{ "& > :not(style)": { ml: 3 } }} className="fab">
          <div className="flex-container">
            <div className="flex-child magenta">
              <div>
                <Fab
                  style={{
                    backgroundColor: "gray",
                    color: "white",
                    marginBottom: 13,
                    marginRight: 10,
                  }}
                  onClick={getPreviousTimesheet}
                >
                  <ArrowBackIcon />
                </Fab>
                <Fab
                  style={{
                    backgroundColor: "gray",
                    color: "white",
                    marginBottom: 13,
                    marginRight: 10,
                  }}
                  onClick={getNextTimesheet}
                >
                  <ArrowForwardIcon />
                </Fab>
              </div>
            </div>

            <div className="flex-child green">
              <div style={{ float: "right" }}>
                <Fab
                  style={{
                    backgroundColor: "rgb(119, 198, 110)",
                    color: "white",
                    marginBottom: 13,
                    marginRight: 10,
                    display:
                      weekInfo.status.toLowerCase() === "pending" ||
                        weekInfo.status.toLowerCase() === "approved"
                        ? "none"
                        : "inline-grid",
                  }}
                  onClick={() => setAddTimesheetEntryOpen(true)}
                >
                  <AddIcon />
                </Fab>
                <Backdrop open={open} />
                <SpeedDial
                  ariaLabel="Menu options"
                  sx={{ display: "inline-flex", position: "relative" }}
                  onClose={handleClose}
                  onOpen={handleOpen}
                  open={open}
                  icon={
                    <SpeedDialIcon
                      icon={<MoreVertIcon />}
                      openIcon={<CloseIcon />}
                    />
                  }
                >
                  {weekInfo.status.toLowerCase() === "new" &&
                    entries.length > 0 && (
                      <SpeedDialAction
                        key="4"
                        icon={<SendIcon />}
                        tooltipTitle="Submit timesheet"
                        tooltipOpen
                        FabProps={{
                          sx: {
                            bgcolor: "rgb(119, 198, 110)",
                            color: "white",
                            "&:hover": {
                              bgcolor: "#c42d57",
                            },
                          },
                        }}
                        onClick={submitTimeSheet}
                      />
                    )}
                  {(weekInfo.status.toLowerCase() === "pending" ||
                    weekInfo.status.toLowerCase() === "rejected") && (
                      <SpeedDialAction
                        key="4"
                        icon={<UnpublishedIcon />}
                        tooltipTitle="Unsubmit timesheet"
                        tooltipOpen
                        FabProps={{
                          sx: {
                            bgcolor: "rgb(172, 5, 51)",
                            color: "white",
                            "&:hover": {
                              bgcolor: "#c42d57",
                            },
                          },
                        }}
                        onClick={unsubmitTimesheet}
                      />
                    )}
                  {weekInfo.status.toLowerCase() === "new" && (
                    <SpeedDialAction
                      key="1"
                      icon={<DeleteIcon />}
                      tooltipTitle="Delete timesheet"
                      tooltipOpen
                      FabProps={{
                        sx: {
                          bgcolor: "rgb(172, 5, 51)",
                          color: "white",
                          "&:hover": {
                            bgcolor: "#c42d57",
                          },
                        },
                      }}
                      onClick={deleteTimesheet}
                    />
                  )}
                  <SpeedDialAction
                    key="2"
                    icon={<ContentCopyIcon />}
                    tooltipTitle="Duplicate timesheet"
                    tooltipOpen
                    FabProps={{
                      sx: {
                        bgcolor: "#4400ff",
                        color: "white",
                        "&:hover": {
                          bgcolor: "#c42d57",
                        },
                      },
                    }}
                    onClick={duplicateWeek}
                  />
                  {weekInfo.status.toLowerCase() === "new" && (
                    <SpeedDialAction
                      key="3"
                      icon={<CreateIcon />}
                      tooltipTitle="Add template entries"
                      tooltipOpen
                      FabProps={{
                        sx: {
                          bgcolor: "rgb(255,69,0)",
                          color: "white",
                          "&:hover": {
                            bgcolor: "#c42d57",
                          },
                        },
                      }}
                      onClick={insertTemplateLine}
                    />
                  )}
                </SpeedDial>
              </div>
            </div>
          </div>
        </Box>
      </div>
    );
  }
}
